import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Input, InputNumber, Row, Select, Space, Typography } from 'antd';
import { campaignsApi } from '../services/catalogServiceAPI/campaignsProxy';
import { SALE_KIND_OPTIONS } from './constants';

const { Text } = Typography;

const STRIPE_HELP = 'A coupon applies to the regular price; a price replaces it. One or the other.';
const APPLE_OFFER_HELP = 'The promotional offer identifier on the premium subscription (App Store Connect → Subscriptions → Promotional Offers). Signed with the In-App Purchase key on the server.';
const GOOGLE_OFFER_HELP = 'The offer id on the premium base plan (Play Console → Subscriptions → base plan → Offers).';
const PACKS_HELP = 'The stores have no coupons for one-time products: each pack on sale is a separate product at the sale price. Leave a store empty to keep its regular packs.';

/**
 * Step one's Sale card: what the campaign discounts and which store offer it
 * points at. The stores hold the offers; this is what picks them for the
 * campaign's audience and window, and what the web checkout charges. Setup
 * steps per store are in docs/campaign-sales.md.
 */
export function SaleCard({ sale, onChange, endAt }) {
    const [packs, setPacks] = useState([]);
    const set = (patch) => onChange({ ...sale, ...patch });
    const setStore = (store, patch) => onChange({ ...sale, [store]: { ...sale[store], ...patch } });
    const setProductId = (store, regularId, saleId) => setStore(store, { productIds: { ...sale[store].productIds, [regularId]: saleId } });

    useEffect(() => {
        if (sale.kind !== 'packs' || packs.length > 0) return;
        campaignsApi.packs().then(setPacks).catch(() => setPacks([]));
    }, [sale.kind, packs.length]);

    const kind = sale.kind || '';

    return (
        <Card title="Sale" size="small" style={{ marginBottom: 16 }}>
            <Form.Item label="What goes on sale" help={kind ? `The sale runs while the campaign is live${endAt ? ' and ends with it' : ''}; the wallet, the premium card and checkout honour it for everyone in the audience, whether or not they saw the dialog.` : 'None: the campaign is a plain announcement.'}>
                <Select value={kind} options={SALE_KIND_OPTIONS} onChange={(value) => set({ kind: value })} style={{ width: 240 }} />
            </Form.Item>

            {(kind === 'premium' || kind === 'packs') && (
                <Space size={16} wrap align="start">
                    <Form.Item label="Percent off" required={kind === 'packs'} help="Shown on every surface; the charged price is the coupon, price or store offer.">
                        <InputNumber min={1} max={99} value={sale.percentOff} onChange={(percentOff) => set({ percentOff })} addonAfter="%" style={{ width: 130 }} />
                    </Form.Item>
                    {kind === 'premium' && (
                        <Form.Item label="Sale price (web)" help="As the web shows it, e.g. $5. Never shown in the apps: they show the store offer's own price.">
                            <Input value={sale.salePriceLabel ?? ''} maxLength={20} onChange={(e) => set({ salePriceLabel: e.target.value })} placeholder="$5" style={{ width: 130 }} />
                        </Form.Item>
                    )}
                </Space>
            )}

            {kind === 'trial' && (
                <Form.Item label="Free days" required help="What Stripe gets and every surface shows. The App Store and Play offers below must be set up with the same length.">
                    <InputNumber min={1} max={365} value={sale.trialDays} onChange={(trialDays) => set({ trialDays })} addonAfter="days" style={{ width: 150 }} />
                </Form.Item>
            )}

            {kind === 'premium' && (
                <>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Web (Stripe)</Text>
                    <Space size={16} wrap align="start">
                        <Form.Item label="Coupon id" help={STRIPE_HELP}>
                            <Input value={sale.stripe.couponId ?? ''} onChange={(e) => setStore('stripe', { couponId: e.target.value })} placeholder="autumn30" style={{ width: 220 }} />
                        </Form.Item>
                        <Form.Item label="Price id" help="A recurring price on the premium product.">
                            <Input value={sale.stripe.priceId ?? ''} onChange={(e) => setStore('stripe', { priceId: e.target.value })} placeholder="price_…" style={{ width: 260 }} />
                        </Form.Item>
                    </Space>
                </>
            )}

            {kind === 'packs' && (
                <>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Web (Stripe)</Text>
                    <Form.Item label="Coupon id" help="Applied to every pack checkout; make it a percent-off coupon matching the number above.">
                        <Input value={sale.stripe.couponId ?? ''} onChange={(e) => setStore('stripe', { couponId: e.target.value })} placeholder="packs20" style={{ width: 220 }} />
                    </Form.Item>
                </>
            )}

            {(kind === 'premium' || kind === 'trial') && (
                <>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Apps</Text>
                    <Space size={16} wrap align="start">
                        <Form.Item label="App Store offer" help={APPLE_OFFER_HELP}>
                            <Input value={sale.apple.offerIdentifier ?? ''} onChange={(e) => setStore('apple', { offerIdentifier: e.target.value })} placeholder="autumn30" style={{ width: 260 }} />
                        </Form.Item>
                        <Form.Item label="Google Play offer" help={GOOGLE_OFFER_HELP}>
                            <Input value={sale.google.offerId ?? ''} onChange={(e) => setStore('google', { offerId: e.target.value })} placeholder="autumn-30" style={{ width: 260 }} />
                        </Form.Item>
                    </Space>
                </>
            )}

            {kind === 'packs' && (
                <>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>Apps</Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>{PACKS_HELP}</Text>
                    <Row gutter={8} style={{ marginBottom: 4 }}>
                        <Col span={8}><Text type="secondary">Regular pack</Text></Col>
                        <Col span={8}><Text type="secondary">App Store sale product</Text></Col>
                        <Col span={8}><Text type="secondary">Google Play sale product</Text></Col>
                    </Row>
                    {packs.length === 0 && <Text type="secondary">Loading the packs…</Text>}
                    {packs.map((pack) => {
                        const regularId = pack.storeProductId ?? pack.appleProductId;
                        return (
                            <Row key={regularId} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                                <Col span={8}>
                                    <Text>{pack.credits} gems</Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>{regularId}</Text>
                                </Col>
                                <Col span={8}>
                                    <Input value={sale.apple.productIds?.[regularId] ?? ''} onChange={(e) => setProductId('apple', regularId, e.target.value)} placeholder={`${regularId}.sale`} />
                                </Col>
                                <Col span={8}>
                                    <Input value={sale.google.productIds?.[regularId] ?? ''} onChange={(e) => setProductId('google', regularId, e.target.value)} placeholder={`${regularId}.sale`} />
                                </Col>
                            </Row>
                        );
                    })}
                </>
            )}
        </Card>
    );
}
